import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileDown, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { PageHeader } from '@/app/shell/PageHeader'
import { useBarriers, useDrift, useIndices, useModules, useNarrative, useRecompute } from '@/lib/api/hooks'
import { BarrierStrip, ModuleRadar, riskColor, tok, type StripBarrier } from '@/charts'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { RiskChip } from '@/ui/chip'
import { DrillBreadcrumb } from '@/ui/drill-breadcrumb'
import { MetricTile } from '@/ui/metric-tile'
import { PackBadge } from '@/ui/pack-badge'
import { VerdictBlock } from '@/ui/verdict-block'
import { EmptyState, ErrorState, LoadingBlock } from '@/ui/states'

const API = import.meta.env.VITE_API_BASE_URL || '/api/v1'

// B0–B5 prevent (pre-event); B6–B9 mitigate/recover (post-event). From the
// reference barrier escalation stages.
const BARRIER_STAGE: Record<string, 'pre' | 'post'> = {
  B0: 'pre', B1: 'pre', B2: 'pre', B3: 'pre', B4: 'pre', B5: 'pre',
  B6: 'post', B7: 'post', B8: 'post', B9: 'post',
}
const BAND_TONE: Record<string, string> = {
  Low: tok('--stable'), Medium: tok('--ember'), High: tok('--flare'), Critical: tok('--ignition'),
}
const n = (v: number | null | undefined) => v ?? 0
const stateWord = (v: number) => (v < 40 ? 'Critical' : v < 55 ? 'Elevated' : v < 70 ? 'Moderate' : v < 80 ? 'Fair' : 'Strong')
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const status404 = (e: unknown) => (e as { response?: { status?: number } })?.response?.status === 404

export function UnderwritingPage() {
  const { role } = useRole()
  const nav = useNavigate()
  const { site, siteId } = useCurrentSite()
  const id = siteId ?? ''
  const indices = useIndices(id)
  const barriers = useBarriers(id)
  const modules = useModules(id)
  const drift = useDrift(id)
  const narrative = useNarrative(id)
  const recompute = useRecompute(id)
  const readonly = role === 'insurer'
  const [tab, setTab] = useState<'Risk summary' | 'Barriers' | 'Modules' | 'Drift' | 'Narrative'>('Risk summary')

  const header = (
    <PageHeader
      title="Underwriting risk summary"
      subtitle={
        <>
          {site && <PackBadge pack={site.pack} />}
          <span>{site?.pack === 'DSEAR' ? '166-question audit' : '267-question audit'}</span>
          {indices.data?.config_version_id != null && (
            <>
              <span className="text-faint">·</span>
              <span className="tabular">config v{indices.data.config_version_id}</span>
            </>
          )}
        </>
      }
      actions={
        <>
          {!readonly && (
            <Button variant="secondary" size="sm" onClick={() => recompute.mutate()} disabled={recompute.isPending}>
              <RefreshCw className={recompute.isPending ? 'animate-spin' : ''} />
              {recompute.isPending ? 'Recomputing…' : 'Recompute'}
            </Button>
          )}
          <Button size="sm" onClick={() => window.open(`${API}/sites/${id}/underwriting/pdf`, '_blank')}>
            <FileDown /> Export PDF
          </Button>
        </>
      }
    />
  )

  const notAssessed = (
    <EmptyState
      kind="no-data"
      title="Not assessed yet"
      action={
        <Button size="sm" onClick={() => nav(`/site/${id}/assessment`)}>
          Start assessment
        </Button>
      }
    >
      This site has no computed underwriting yet. Answer questions in the assessment to compute the eight
      indices and the barrier profile.
    </EmptyState>
  )

  if (indices.isLoading) {
    return (
      <>
        {header}
        <Card className="p-6">
          <LoadingBlock lines={5} />
        </Card>
      </>
    )
  }

  // No computed output. Distinguish the two honest cases behind a 404:
  //  - petrochem (DSEAR): the engine metadata is in progress (degraded pack).
  //  - BESS: the site simply hasn't been assessed yet.
  if (indices.isError) {
    const is404 = status404(indices.error)
    const petro = site?.pack === 'DSEAR'
    return (
      <>
        {header}
        {is404 && petro ? (
          <EmptyState kind="degraded" title="Engine metadata in progress">
            The eight engine indices, barriers and drift require pack barrier/decay metadata still being
            finalised for this site. Base signals (coverage, evidence, obligations) render elsewhere —
            shown transparently, never as a fake zero.
          </EmptyState>
        ) : is404 ? (
          notAssessed
        ) : (
          <ErrorState onRetry={() => indices.refetch()} />
        )}
      </>
    )
  }

  const idx = indices.data!
  // An output row can persist with nothing actually assessed (e.g. every answer
  // withdrawn): control_confidence is null. Show the honest unassessed state rather
  // than rendering null indices as "0 / Strong".
  if (idx.control_confidence_percent == null) {
    return (
      <>
        {header}
        {notAssessed}
      </>
    )
  }
  const cc = n(idx.control_confidence_percent)
  const strip: StripBarrier[] = barriers.data
    ? Object.entries(barriers.data)
        .map(([code, b]) => ({
          code,
          confidence: n(b.avg_confidence),
          threshold: n(b.threshold),
          stage: BARRIER_STAGE[code] ?? 'pre',
        }))
        .sort((a, b) => a.code.localeCompare(b.code))
    : []
  const radar = modules.data
    ? Object.entries(modules.data)
        .map(([m, v]) => ({ module: m, value: n(v.avg_confidence) }))
        .sort((a, b) => a.module.localeCompare(b.module))
    : []
  const barrierRows = barriers.data
    ? Object.entries(barriers.data)
        .map(([code, b]) => ({ code, ...b, stage: BARRIER_STAGE[code] ?? 'pre' }))
        .sort((a, b) => a.code.localeCompare(b.code))
    : []
  const moduleRows = modules.data
    ? Object.entries(modules.data)
        .map(([m, v]) => ({ module: m, ...v }))
        .sort((a, b) => a.module.localeCompare(b.module))
    : []
  const concerns = (narrative.data?.key_concerns ?? []) as string[]

  const tiles: { label: string; value: string; unit?: string; tv: number; state?: string }[] = [
    { label: 'Control confidence', value: String(cc), unit: '/100', tv: cc },
    { label: 'RiskScore', value: String(n(idx.risk_score)), tv: 100 - n(idx.risk_score) },
    { label: 'Loss probability', value: `${n(idx.loss_probability_percent)}%`, tv: 100 - n(idx.loss_probability_percent), state: idx.loss_probability_label ?? undefined },
    { label: 'Loss Driver Index', value: String(n(idx.loss_driver_index)), tv: 100 - n(idx.loss_driver_index) },
    { label: 'Escalation potential', value: String(n(idx.explosion_escalation_potential)), tv: 100 - n(idx.explosion_escalation_potential) },
    { label: 'Dependency concentration', value: String(n(idx.dependency_concentration)), tv: 100 - n(idx.dependency_concentration) },
    { label: 'Recovery fragility', value: String(n(idx.recovery_fragility)), tv: 100 - n(idx.recovery_fragility) },
    { label: 'Protection validity', value: String(n(idx.protection_validity)), unit: '/100', tv: n(idx.protection_validity) },
  ]

  const driftState = idx.drift_state ?? 'stable'
  const declining = /critical|declin|latent/i.test(driftState)

  return (
    <>
      {header}

      <Card className="p-6">
        <div className="grid items-start gap-6 md:grid-cols-[236px_1fr]">
          <VerdictBlock
            lossProbability={`${n(idx.loss_probability_percent)}%`}
            band={idx.loss_probability_label ?? '—'}
            bandTone={BAND_TONE[idx.loss_probability_label ?? ''] ?? tok('--ember')}
            exposure={String(n(idx.exposure_rating))}
            control={cc}
            controlTone={riskColor(cc)}
            critical={idx.critical_risk_flag ? (idx.critical_risk_reason ?? 'Critical risk flag raised.') : null}
            drift={{ label: `${declining ? '↘' : '→'} ${cap(driftState)}`, tone: declining ? tok('--flare') : tok('--stable') }}
          />
          {barriers.isLoading ? <LoadingBlock lines={4} /> : <BarrierStrip barriers={strip} />}
        </div>
      </Card>

      <div className="my-5 flex flex-wrap gap-1 border-b">
        {(['Risk summary', 'Barriers', 'Modules', 'Drift', 'Narrative'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              '-mb-px px-3 py-2.5 text-[13px] font-semibold',
              tab === t ? 'border-b-2 border-primary text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Risk summary' && (
        <>
          <div className="mb-3 font-display text-[13px] font-semibold">
            Eight underwriting indices{' '}
            <span className="text-[11.5px] font-normal text-faint">— maturity {idx.maturity_band ?? '—'} · exposure {idx.exposure_label ?? '—'}</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {tiles.map((t) => (
              <MetricTile
                key={t.label}
                label={t.label}
                value={t.value}
                unit={t.unit}
                tone={riskColor(t.tv)}
                state={t.state ?? stateWord(t.tv)}
                onDrill={() => nav(`/site/${id}/evidence`)}
              />
            ))}
          </div>
          {idx.critical_risk_flag && idx.critical_risk_reason && (
            <>
              <div className="mb-3 mt-6 font-display text-[13px] font-semibold">Why it&rsquo;s flagged</div>
              <DrillBreadcrumb
                steps={[
                  { label: 'Risk summary', onClick: () => setTab('Risk summary') },
                  { label: 'Barriers', onClick: () => setTab('Barriers') },
                  { label: 'Modules', onClick: () => setTab('Modules') },
                  { label: 'Control', active: true },
                  { label: 'Evidence', active: true, onClick: () => nav(`/site/${id}/evidence`) },
                ]}
              />
              <p className="mt-3 max-w-2xl text-[13.5px] text-muted-foreground">{idx.critical_risk_reason}</p>
            </>
          )}
        </>
      )}

      {tab === 'Barriers' && (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-faint">
                <th className="px-4 py-3 font-semibold">Barrier</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3 font-semibold">Confidence</th>
                <th className="px-4 py-3 font-semibold">Threshold</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {barrierRows.map((b) => (
                <tr key={b.code} className="border-t">
                  <td className="px-4 py-2.5 font-medium">{b.code}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {b.stage === 'pre' ? 'Prevention' : 'Mitigation & recovery'}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 rounded bg-surface-2">
                        <div className="h-full rounded" style={{ width: `${n(b.avg_confidence)}%`, background: riskColor(n(b.avg_confidence)) }} />
                      </div>
                      <span className="tabular text-[12px]" style={{ color: riskColor(n(b.avg_confidence)) }}>{n(b.avg_confidence)}</span>
                    </div>
                  </td>
                  <td className="tabular px-4 py-2.5">{n(b.threshold)}</td>
                  <td className="px-4 py-2.5">{b.status && <RiskChip label={b.status} tone={riskColor(n(b.avg_confidence))} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'Modules' && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <KLabel className="mb-1">Module profile M1–M6</KLabel>
            <ModuleRadar data={radar} />
          </Card>
          <Card className="overflow-hidden p-0">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-faint">
                  <th className="px-4 py-3 font-semibold">Module</th>
                  <th className="px-4 py-3 font-semibold">Confidence</th>
                  <th className="px-4 py-3 font-semibold">Questions</th>
                </tr>
              </thead>
              <tbody>
                {moduleRows.map((m) => (
                  <tr key={m.module} className="border-t">
                    <td className="px-4 py-2.5 font-medium">{m.module}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-24 rounded bg-surface-2">
                          <div className="h-full rounded" style={{ width: `${n(m.avg_confidence)}%`, background: riskColor(n(m.avg_confidence)) }} />
                        </div>
                        <span className="tabular text-[12px]" style={{ color: riskColor(n(m.avg_confidence)) }}>{n(m.avg_confidence)}</span>
                      </div>
                    </td>
                    <td className="tabular px-4 py-2.5 text-muted-foreground">{n(m.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {tab === 'Drift' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(
            [
              ['Drift index', n(drift.data?.drift_index), declining ? tok('--flare') : tok('--stable')],
              ['Drift state', cap(driftState), undefined],
              ['Recovery state', cap(idx.recovery_state ?? '—'), undefined],
              ['Recovery fragility', n(idx.recovery_fragility), undefined],
              ['Active overrides', n(drift.data?.active_overrides), undefined],
              ['Active impairments', n(drift.data?.active_impairments), undefined],
              ['Significant decay', n(drift.data?.significant_decay), undefined],
              ['Low confidence', n(drift.data?.low_confidence), undefined],
            ] as const
          ).map(([label, value, colr]) => (
            <Card key={label}>
              <KLabel>{label}</KLabel>
              <div className="mt-2 font-display text-[24px] font-semibold tabular" style={colr ? { color: colr } : undefined}>
                {value}
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'Narrative' && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="md:col-span-2">
            <KLabel className="mb-2">Executive summary</KLabel>
            <p className="text-[13.5px] leading-relaxed">
              {narrative.data?.executive_summary ?? 'Narrative unavailable.'}
            </p>
          </Card>
          {narrative.data?.loss_driver_summary && (
            <Card>
              <KLabel className="mb-2">Loss drivers</KLabel>
              <p className="text-[13.5px] leading-relaxed">{narrative.data.loss_driver_summary}</p>
            </Card>
          )}
          {concerns.length > 0 && (
            <Card>
              <KLabel className="mb-2">Key concerns</KLabel>
              <ul className="space-y-1.5 text-[13.5px]">
                {concerns.map((c, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-flare">•</span> {c}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <div className="text-[11px] text-faint md:col-span-2">Generated from engine outputs · no figures invented</div>
        </div>
      )}
    </>
  )
}
